import { Fragment, type ReactNode } from 'react';
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Popper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { skipToken } from '@reduxjs/toolkit/query';
import type { Dayjs } from '@/config/dayjs';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useLocalization } from '@/hooks/useLocalization';
import i18n from '@/i18n';
import NetWorthChangeValue from '@/modules/advisor/netWorthChanges/NetWorthChangeValue';
import { useGetWealthReportedTrendDetailQuery } from '@/store/advisorWealthTrendApi';
import { formatNumberToCurrency } from '@/utils/currency';
import {
  APP_DATE_FORMAT,
  APP_LONG_DATE_FORMAT,
  formatAppDate,
} from '@/utils/date';
import { comparisonDateFor, windowChange } from './wealthTrendMath';

/** What the popover is about: one series on one day, and where to draw it. */
export interface WealthTrendPopoverPoint {
  isHome: boolean;
  day: Dayjs;
  /** The series' value on that day, which the window ends on. */
  value: number;
  anchor: { top: number; left: number };
}

interface WealthTrendPopoverProps {
  point: WealthTrendPopoverPoint | null;
  onClose: () => void;
}

/** Space between the popover and its point, on whichever side it opens. */
const POINTER_GAP_PX = 12;

/**
 * Keeps the popover clear of its point on both sides. It opens above the point,
 * and flips below when there is no room; without a gap there, it opened right
 * under the pointer, so pressing to start a drag landed on the popover rather
 * than the chart.
 */
const POPPER_MODIFIERS = [
  { name: 'offset', options: { offset: [0, POINTER_GAP_PX] } },
];

/**
 * A zero-size anchor at the point the popover belongs to. The popper reads its
 * position on each update, so the popover follows the point's screen spot.
 */
function anchorAt(anchor: { top: number; left: number }) {
  return {
    getBoundingClientRect: () => new DOMRect(anchor.left, anchor.top, 0, 0),
  };
}

/** Whole dollars, as legacy's popover shows them; a gain carries no `+`. */
function wholeDollars(value: number): string {
  return formatNumberToCurrency(value, 'USD', 0);
}

/** A percentage, unsigned for a gain as in legacy, or null when there is none. */
function percent(value: number | null): string | null {
  if (value === null) return null;
  return `${value.toFixed(2)}%`;
}

/** The change type the advisor endpoint always reports. */
const ADVISOR_CHANGE_TYPE = 'Clients';

/**
 * The right panel's change type, translated. The advisor endpoint sends the
 * English `Clients`, which is shown through the locale string; any other type
 * is passed through as sent.
 */
function changeTypeLabel(
  t: ReturnType<typeof useLocalization>['t'],
  type: string | null | undefined,
): string {
  if (!type || type === ADVISOR_CHANGE_TYPE) {
    return t('wealthTrend.clients', 'Clients');
  }
  return type;
}

/** The two panels' headings: the window on the left, the clients on the right. */
const PANEL_HEADING_SX = { mb: 1.5 };

interface ValueRowProps {
  label: string;
  children: ReactNode;
}

/** A Beginning / End / Change row: label left, value right. */
function ValueRow({ label, children }: ValueRowProps) {
  return (
    <Stack
      direction='row'
      justifyContent='space-between'
      alignItems='flex-start'
      gap={2}
    >
      <Typography variant='body2'>{label}</Typography>
      {children}
    </Stack>
  );
}

/**
 * The hovered point's detail, as legacy's `HistoricalTrendFinancialTooltip`
 * shows it.
 *
 * Left: the window the point closes -- From its start to the hovered day --
 * with the Beginning value, the End value and the Change between them. The
 * Beginning comes from the details endpoint, which falls back to the window's
 * earliest point when its first day has none. Until it answers, Beginning and
 * Change show a placeholder rather than the day's own value, which would read
 * as no change; if it fails they show a dash.
 *
 * Right: the clients whose balance moved most that day against the day
 * before, or "No Changes" when none did. Names wrap between words, never
 * inside one.
 *
 * As in legacy, gains carry no `+` -- the colour says which way a value went --
 * and only the window's percentage change has the up or down caret.
 *
 * Asks for nothing, and shows nothing, while authentication is not loaded, so
 * a logout or user switch with it open never re-sends the old point's request.
 *
 * Non-modal, unlike a Popover: it puts no backdrop over the page, so the
 * chart stays live underneath and another point can be hovered or clicked to
 * move it, and clicking elsewhere leaves it open. Only × closes it, as the
 * ticket asks.
 */
export default function WealthTrendPopover({
  point,
  onClose,
}: WealthTrendPopoverProps) {
  const { t } = useLocalization();
  const comparison = point ? comparisonDateFor(point.day) : null;

  const authLoaded = useAuthIsLoaded();
  const { currentData, isFetching, isError } =
    useGetWealthReportedTrendDetailQuery(
      authLoaded && point && comparison
        ? {
            selectedDate: formatAppDate(point.day, { format: APP_DATE_FORMAT }),
            comparisonDate: formatAppDate(comparison, {
              format: APP_DATE_FORMAT,
            }),
            isHome: point.isHome,
          }
        : skipToken,
    );

  if (!authLoaded || !point || !comparison) return null;

  const trends = currentData?.Trends ?? [];
  const opening = trends.length > 0 ? trends[trends.length - 1] : undefined;
  const beginning = opening?.Value ?? point.value;
  const change = windowChange(beginning, point.value);
  const changes = currentData?.Changes ?? [];
  const isOpeningPending = currentData === undefined;

  const renderOpeningPending = () => {
    if (isError) {
      return <Typography variant='body2'>{t('common.emdash', '—')}</Typography>;
    }
    return (
      <Skeleton
        variant='text'
        width={64}
        data-testid='wealth-trend-opening-loading'
      />
    );
  };

  const renderBeginning = () => {
    if (isOpeningPending) return renderOpeningPending();
    return <Typography variant='body2'>{wholeDollars(beginning)}</Typography>;
  };

  const renderChange = () => {
    if (isOpeningPending) return renderOpeningPending();
    return (
      <Stack alignItems='flex-end'>
        <NetWorthChangeValue
          value={change.dollarChange}
          text={wholeDollars(change.dollarChange)}
        />
        <NetWorthChangeValue
          value={change.dollarChange}
          text={percent(change.percentChange)}
          caret
        />
      </Stack>
    );
  };

  const renderChanges = () => {
    if (isError) {
      return (
        <Typography color='error'>
          {t('errorLoadingData', 'Error loading data.')}
        </Typography>
      );
    }
    if (isFetching && !currentData) {
      return (
        <Skeleton
          variant='rounded'
          height={96}
        />
      );
    }
    if (changes.length === 0) {
      return (
        <Typography variant='subtitle2'>
          {t('wealthTrend.noChanges', 'No Changes')}
        </Typography>
      );
    }
    return (
      <Box>
        <Typography
          variant='subtitle2'
          sx={PANEL_HEADING_SX}
        >
          {t(
            'wealthTrend.biggestDailyChanges',
            '{{count}} Biggest Daily Changes ({{type}})',
            {
              count: changes.length,
              type: changeTypeLabel(t, currentData?.BiggestChangeType),
            },
          )}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto auto',
            columnGap: 1.5,
            rowGap: 0.75,
            alignItems: 'baseline',
          }}
        >
          {changes.map((client) => (
            <Fragment key={client.Id}>
              <Typography
                variant='body2'
                sx={{ overflowWrap: 'break-word' }}
              >
                {client.Name}
              </Typography>
              <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <NetWorthChangeValue
                  value={client.PercentChange}
                  text={percent(client.PercentChange)}
                />
              </Box>
              <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <NetWorthChangeValue
                  value={client.DollarChange}
                  text={wholeDollars(client.DollarChange)}
                />
              </Box>
            </Fragment>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Popper
      open
      anchorEl={anchorAt(point.anchor)}
      placement='top'
      modifiers={POPPER_MODIFIERS}
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      data-testid='wealth-trend-popover'
    >
      <Paper
        elevation={8}
        sx={{ width: { xs: 320, sm: 540 } }}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{ px: 2, py: 1.5 }}
        >
          <Typography variant='subtitle1'>
            {formatAppDate(point.day, {
              format: APP_LONG_DATE_FORMAT,
              locale: i18n.language,
            })}
          </Typography>
          <IconButton
            size='small'
            aria-label={t('close', 'Close')}
            onClick={onClose}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Stack>
        <Divider />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          divider={
            <Divider
              orientation='vertical'
              flexItem
            />
          }
          gap={2}
          sx={{ p: 2 }}
        >
          <Box sx={{ minWidth: 200 }}>
            <Typography
              variant='subtitle2'
              sx={PANEL_HEADING_SX}
            >
              {t('wealthTrend.fromTo', 'From {{start}} to {{end}}', {
                start: formatAppDate(opening?.Date ?? comparison),
                end: formatAppDate(point.day),
              })}
            </Typography>
            <Stack gap={0.75}>
              <ValueRow label={t('wealthTrend.beginning', 'Beginning')}>
                {renderBeginning()}
              </ValueRow>
              <ValueRow label={t('wealthTrend.end', 'End')}>
                <Typography
                  variant='body2'
                  sx={{ fontWeight: 'bold' }}
                >
                  {wholeDollars(point.value)}
                </Typography>
              </ValueRow>
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            <ValueRow label={t('wealthTrend.change', 'Change')}>
              {renderChange()}
            </ValueRow>
          </Box>
          <Box sx={{ flex: 1 }}>{renderChanges()}</Box>
        </Stack>
      </Paper>
    </Popper>
  );
}
